import {
  locateTrackPosition,
  sampleCenterlineAhead,
  type Track,
} from '../track/TrackLoader';

export type TrackAIPathSegmentKind = 'straight' | 'entry' | 'apex' | 'exit';

export interface TrackAIPathSample {
  arc: number;
  x: number;
  z: number;
  tx: number;
  tz: number;
  nx: number;
  nz: number;
  widthLeft: number;
  widthRight: number;
  lateralOffset: number;
  signedCurvature: number;
  curvature: number;
  targetSpeed: number;
  lookahead: number;
  segmentKind: TrackAIPathSegmentKind;
}

export interface TrackAIPath {
  samples: TrackAIPathSample[];
  spacing: number;
  totalLength: number;
}

export interface TrackAIPathOptions {
  spacing?: number;
  safetyMargin?: number;
  maxSpeed?: number;
  minSpeed?: number;
  lateralAcceleration?: number;
  brakingAcceleration?: number;
  acceleration?: number;
  racingLineStrength?: number;
  laneOffset?: number;
}

const DEFAULT_SPACING = 2.5;
const DEFAULT_SAFETY_MARGIN = 3.2;
const DEFAULT_MAX_SPEED = 76;
const DEFAULT_MIN_SPEED = 38;
const DEFAULT_LATERAL_ACCELERATION = 180;
const DEFAULT_BRAKING_ACCELERATION = 70;
const DEFAULT_ACCELERATION = 48;
const CURVATURE_EPSILON = 1e-4;

const pathCache = new WeakMap<Track, Map<string, TrackAIPath>>();

export function getTrackAIPath(track: Track, options: TrackAIPathOptions = {}): TrackAIPath {
  const key = cacheKey(options);
  let trackCache = pathCache.get(track);
  if (!trackCache) {
    trackCache = new Map();
    pathCache.set(track, trackCache);
  }
  const cached = trackCache.get(key);
  if (cached) return cached;
  const path = buildTrackAIPath(track, options);
  trackCache.set(key, path);
  return path;
}

export function buildTrackAIPath(track: Track, options: TrackAIPathOptions = {}): TrackAIPath {
  const spacing = options.spacing ?? DEFAULT_SPACING;
  const count = Math.max(16, Math.ceil(track.totalLength / spacing));
  const actualSpacing = track.totalLength / count;
  const safetyMargin = options.safetyMargin ?? DEFAULT_SAFETY_MARGIN;
  const maxSpeed = options.maxSpeed ?? DEFAULT_MAX_SPEED;
  const minSpeed = options.minSpeed ?? DEFAULT_MIN_SPEED;
  const lateralAcceleration = options.lateralAcceleration ?? DEFAULT_LATERAL_ACCELERATION;
  const racingLineStrength = options.racingLineStrength ?? 0.32;
  const laneOffset = options.laneOffset ?? 0;

  const centers = Array.from({ length: count }, (_, i) => {
    const arc = i * actualSpacing;
    const center = sampleCenterlineAhead(track, 0, arc);
    const pos = locateTrackPosition(track, center.x, center.z);
    const signedCurvature = signedCurvatureAt(track, arc, actualSpacing * 2);
    return { arc, center, pos, signedCurvature };
  });

  const rawInsideOffsets = centers.map(({ pos, signedCurvature }) => {
    const magnitude = clamp((Math.abs(signedCurvature) - 0.004) / 0.035, 0, 1);
    if (magnitude <= 0) return 0;
    const usableLeft = Math.max(0, pos.widthLeft - safetyMargin);
    const usableRight = Math.max(0, pos.widthRight - safetyMargin);
    const inside = signedCurvature >= 0 ? usableLeft : -usableRight;
    return inside * racingLineStrength * magnitude;
  });

  const offsets = rawInsideOffsets.map((inside, i) => {
    const ahead = rawInsideOffsets[(i + 6) % count];
    const behind = rawInsideOffsets[(i - 6 + count) % count];
    const shaped = inside * 0.9 - ahead * 0.32 - behind * 0.18 + laneOffset;
    const pos = centers[i].pos;
    return clamp(shaped, -Math.max(0, pos.widthRight - safetyMargin), Math.max(0, pos.widthLeft - safetyMargin));
  });
  smoothCircular(offsets, 4);

  const samples: TrackAIPathSample[] = centers.map(({ arc, center, pos, signedCurvature }, i) => {
    const lateralOffset = clamp(
      offsets[i],
      -Math.max(0, pos.widthRight - safetyMargin),
      Math.max(0, pos.widthLeft - safetyMargin),
    );
    const x = center.x + pos.nx * lateralOffset;
    const z = center.z + pos.nz * lateralOffset;
    return {
      arc,
      x,
      z,
      tx: center.tx,
      tz: center.tz,
      nx: pos.nx,
      nz: pos.nz,
      widthLeft: pos.widthLeft,
      widthRight: pos.widthRight,
      lateralOffset,
      signedCurvature,
      curvature: Math.abs(signedCurvature),
      targetSpeed: maxSpeed,
      lookahead: 14,
      segmentKind: segmentKindFor(signedCurvature, rawInsideOffsets, i),
    };
  });

  updatePathTangentsAndCurvature(samples);
  for (const sample of samples) {
    const curvatureSpeed = sample.curvature > CURVATURE_EPSILON
      ? Math.sqrt(lateralAcceleration / sample.curvature)
      : maxSpeed;
    sample.targetSpeed = clamp(Math.min(curvatureSpeed, maxSpeed), minSpeed, maxSpeed);
  }
  smoothSpeeds(samples, 2);
  applySpeedEnvelope(samples, actualSpacing, options);
  for (const sample of samples) sample.lookahead = lookaheadFor(sample.curvature, sample.targetSpeed);

  return { samples, spacing: actualSpacing, totalLength: track.totalLength };
}

export function sampleTrackAIPath(path: TrackAIPath, arc: number): TrackAIPathSample {
  const total = Math.max(1e-6, path.totalLength);
  const wrapped = wrapArc(arc, total);
  const rawIndex = wrapped / path.spacing;
  const i = Math.floor(rawIndex) % path.samples.length;
  const next = (i + 1) % path.samples.length;
  const t = rawIndex - Math.floor(rawIndex);
  const a = path.samples[i];
  const b = path.samples[next];
  const tx = lerp(a.tx, b.tx, t);
  const tz = lerp(a.tz, b.tz, t);
  const tangentLen = Math.hypot(tx, tz) || 1;
  const nx = lerp(a.nx, b.nx, t);
  const nz = lerp(a.nz, b.nz, t);
  const normalLen = Math.hypot(nx, nz) || 1;
  return {
    arc: wrapped,
    x: lerp(a.x, b.x, t),
    z: lerp(a.z, b.z, t),
    tx: tx / tangentLen,
    tz: tz / tangentLen,
    nx: nx / normalLen,
    nz: nz / normalLen,
    widthLeft: lerp(a.widthLeft, b.widthLeft, t),
    widthRight: lerp(a.widthRight, b.widthRight, t),
    lateralOffset: lerp(a.lateralOffset, b.lateralOffset, t),
    signedCurvature: lerp(a.signedCurvature, b.signedCurvature, t),
    curvature: lerp(a.curvature, b.curvature, t),
    targetSpeed: lerp(a.targetSpeed, b.targetSpeed, t),
    lookahead: lerp(a.lookahead, b.lookahead, t),
    segmentKind: t < 0.5 ? a.segmentKind : b.segmentKind,
  };
}

function updatePathTangentsAndCurvature(samples: TrackAIPathSample[]): void {
  const n = samples.length;
  for (let i = 0; i < n; i++) {
    const prev = samples[(i - 1 + n) % n];
    const next = samples[(i + 1) % n];
    const dx = next.x - prev.x;
    const dz = next.z - prev.z;
    const len = Math.hypot(dx, dz) || 1;
    samples[i].tx = dx / len;
    samples[i].tz = dz / len;
    samples[i].nx = -samples[i].tz;
    samples[i].nz = samples[i].tx;
  }
  for (let i = 0; i < n; i++) {
    const prev = samples[(i - 2 + n) % n];
    const next = samples[(i + 2) % n];
    const span = Math.max(1, samples[i].arc - prev.arc || samples[1]?.arc || 1);
    const signed = tangentAngleDelta(prev.tx, prev.tz, next.tx, next.tz) / (span * 2);
    samples[i].signedCurvature = signed;
    samples[i].curvature = Math.abs(signed);
  }
}

function applySpeedEnvelope(samples: TrackAIPathSample[], spacing: number, options: TrackAIPathOptions): void {
  const brake = options.brakingAcceleration ?? DEFAULT_BRAKING_ACCELERATION;
  const accel = options.acceleration ?? DEFAULT_ACCELERATION;
  for (let pass = 0; pass < 4; pass++) {
    for (let i = samples.length - 1; i >= 0; i--) {
      const next = samples[(i + 1) % samples.length];
      samples[i].targetSpeed = Math.min(
        samples[i].targetSpeed,
        Math.sqrt(next.targetSpeed * next.targetSpeed + 2 * brake * spacing),
      );
    }
    for (let i = 0; i < samples.length; i++) {
      const prev = samples[(i - 1 + samples.length) % samples.length];
      samples[i].targetSpeed = Math.min(
        samples[i].targetSpeed,
        Math.sqrt(prev.targetSpeed * prev.targetSpeed + 2 * accel * spacing),
      );
    }
  }
}

function smoothSpeeds(samples: TrackAIPathSample[], passes: number): void {
  for (let pass = 0; pass < passes; pass++) {
    const next = samples.map((sample, i) => {
      const prev = samples[(i - 1 + samples.length) % samples.length];
      const after = samples[(i + 1) % samples.length];
      return sample.targetSpeed * 0.5 + (prev.targetSpeed + after.targetSpeed) * 0.25;
    });
    for (let i = 0; i < samples.length; i++) samples[i].targetSpeed = next[i];
  }
}

function smoothCircular(values: number[], passes: number): void {
  for (let pass = 0; pass < passes; pass++) {
    const next = values.map((value, i) => {
      const prev = values[(i - 1 + values.length) % values.length];
      const after = values[(i + 1) % values.length];
      return value * 0.5 + (prev + after) * 0.25;
    });
    for (let i = 0; i < values.length; i++) values[i] = next[i];
  }
}

function segmentKindFor(
  signedCurvature: number,
  rawInsideOffsets: number[],
  index: number,
): TrackAIPathSegmentKind {
  if (Math.abs(signedCurvature) < 0.004) return 'straight';
  const here = Math.abs(rawInsideOffsets[index]);
  const ahead = Math.abs(rawInsideOffsets[(index + 5) % rawInsideOffsets.length]);
  const behind = Math.abs(rawInsideOffsets[(index - 5 + rawInsideOffsets.length) % rawInsideOffsets.length]);
  if (here >= ahead && here >= behind) return 'apex';
  if (ahead > here) return 'entry';
  return 'exit';
}

function signedCurvatureAt(track: Track, arc: number, span: number): number {
  const a = sampleCenterlineAhead(track, arc, -span);
  const b = sampleCenterlineAhead(track, arc, span);
  return tangentAngleDelta(a.tx, a.tz, b.tx, b.tz) / Math.max(1, span * 2);
}

function lookaheadFor(curvature: number, speed: number): number {
  const speedLookahead = 8 + speed * 0.24;
  const curveScale = 1 - clamp((curvature - 0.01) / 0.06, 0, 0.5);
  return clamp(speedLookahead * curveScale, 8, 24);
}

function cacheKey(options: TrackAIPathOptions): string {
  return [
    options.spacing,
    options.safetyMargin,
    options.maxSpeed,
    options.minSpeed,
    options.lateralAcceleration,
    options.brakingAcceleration,
    options.acceleration,
    options.racingLineStrength,
    options.laneOffset,
  ].join(':');
}

function wrapArc(arc: number, total: number): number {
  return ((arc % total) + total) % total;
}

function tangentAngleDelta(ax: number, az: number, bx: number, bz: number): number {
  return Math.atan2(ax * bz - az * bx, ax * bx + az * bz);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
